import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

interface Props {
  value:    number;
  years:    number[];
  onChange: (y: number) => void;
}

const YearSelect = ({ value, years, onChange }: Props) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm font-medium bg-white text-textPrimary cursor-pointer transition-all
          ${open ? 'border-textPrimary/40 ring-1 ring-textPrimary/20' : 'border-borderDivider hover:border-textPrimary/30'}`}
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 text-textSecondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            className="absolute left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-borderDivider rounded-lg shadow-lg z-10 py-1"
          >
            {years.map(y => {
              const selected = y === value;
              return (
                <li key={y}>
                  <button
                    type="button"
                    onClick={() => { onChange(y); setOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-sm transition-colors
                      ${selected
                        ? 'bg-textPrimary/10 text-textPrimary font-semibold'
                        : 'text-textPrimary hover:bg-gray-50'}`}
                  >
                    {y}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default YearSelect;
